import { debounce } from '@mobi/utils/functions'
import { toggleIsHamburgerMenuOpen } from '@core/Areas/AppHeader/Store'
import { showLogin } from '@core/Areas/Login/helpers/showLogin'
import { navigateToSkyRacePage } from '@core/Areas/RaceCardSky/helpers/navigateToSkyRacePage'
import { getEntitlementUrlFromApi } from '@core/Areas/Racing/Components/RaceReplay/api'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { store } from '@core/Store'
import { logError } from '@core/Utils/logging/logging'
import { displayVisionNotEntitledMessage } from '../Components/VideoPlayer/helpers/useEntitlement'
import { SkyVideoPlayerState, setCloseSkyVideoPlayer, setSkyVideoPlayerStatus } from '../Store'
import { selectIsSkyVideoPlayerOpen } from '../Store/selectors'
import { trackVisionOpened } from '../analytics'
import { fetchAndStoreSkySecureUrl } from './skySecureUrlService'

export function toggleSkyVideoPlayer({
  selectedChannel,
  playerStatus,
  isPlayerOpen,
  analyticsData,
  raceReplayKey,
  raceReplayUrl,
  raceReplayTitle,
  raceReplayMeetingDate,
}: StartSkyVideoPlayerOptions = {}): void {
  if (
    isPlayerOpen === false ||
    (isPlayerOpen === undefined && selectIsSkyVideoPlayerOpen(store.getState()))
  ) {
    store.dispatch(setCloseSkyVideoPlayer())
    return
  }

  store.dispatch(toggleIsHamburgerMenuOpen(false))

  userAccountState$.take(1).subscribe(({ isLoggedIn }) => {
    const openSkyVideoPlayer = async () => {
      if (raceReplayKey && selectedChannel && selectedChannel === 'Replay') {
        try {
          const entitlementData = await queryClient.fetchQuery({
            queryKey: queryKeys.videoEntitlementRequest(raceReplayKey),
            queryFn: () => getEntitlementUrlFromApi(raceReplayKey),
            staleTime: 30_000,
          })
          if (!entitlementData.IsEntitled) {
            store.dispatch(setCloseSkyVideoPlayer())

            const notEntitledMessage = entitlementData.ReasonText
              ? entitlementData.ReasonText
              : raceReplayKey
                ? 'You are not authorised to view this race replay'
                : 'You are not authorised to view SKY Racing vision'

            displayVisionNotEntitledMessage(notEntitledMessage)
            return
          }
          if (!entitlementData.VideoKey) {
            logError(
              `getEntitlementUrlFromApi: url is empty: raceReplayKey=${raceReplayKey}, entitlementData=${JSON.stringify(entitlementData)}`,
              true
            )
            return
          }
          raceReplayUrl = entitlementData.VideoKey
        } catch (err) {
          logError(
            err instanceof Error
              ? err
              : `getEntitlementUrlFromApi: failed to retrieve: raceReplayKey=${raceReplayKey}`,
            true
          )
          return
        }
      }

      // Fetch and cache secure URL for Sky1/Sky2 channels
      if (selectedChannel === 'Sky1' || selectedChannel === 'Sky2') {
        await fetchAndStoreSkySecureUrl(selectedChannel)
      }

      store.dispatch(
        setSkyVideoPlayerStatus({
          playerStatus: playerStatus || 'floating',
          selectedChannel,
          raceReplayKey,
          raceReplayUrl,
          raceReplayTitle,
          raceReplayMeetingDate,
        })
      )
    }

    if (isLoggedIn) {
      openSkyVideoPlayer()
      if (analyticsData) trackVisionOpened(analyticsData)
    } else {
      const handleOnLoginSuccess = () => {
        if (analyticsData?.openLocation === 'bottom-navigation') {
          navigateToSkyRacePage()
        } else openSkyVideoPlayer()
      }
      showLogin({ onLoginSuccessCallback: debounce(handleOnLoginSuccess, 500) })
    }
  })
}

// =====
// Types
// =====

type StartSkyVideoPlayerOptions = {
  analyticsData?: Parameters<typeof trackVisionOpened>[0]
} & Partial<
  Pick<
    SkyVideoPlayerState,
    | 'selectedChannel'
    | 'playerStatus'
    | 'isPlayerOpen'
    | 'raceReplayKey'
    | 'raceReplayUrl'
    | 'raceReplayTitle'
    | 'raceReplayMeetingDate'
  >
>
